import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Activity, Bell, BellOff, ChevronDown, Database, ExternalLink, FolderKanban, GraduationCap, LayoutDashboard,
  LogOut, Menu, Monitor, Moon, RefreshCw, ScrollText, Search, Settings2, Siren, Sun, X, Computer,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { GROEPEN } from '../../gedeeld/types'
import { api, uitloggen } from '../lib/api'
import { geleden, initialen } from '../lib/opmaak'
import { Link, navigeer, useKop, useRoute } from '../lib/router'
import { laadOverzicht, meld, meldFout, useNu, useOverzicht, useStaat } from '../lib/staat'
import { kiesThema, THEMA_LABELS, useThema, type ThemaKeuze } from '../lib/thema'
import { Knop, Logo, StaatIcoon } from './ui'
import Meldingen from './Meldingen'

/* ===========================================================================
 *  Het geraamte -- dat van het dashboard en Projects
 *
 *  Een witte balk van 48 hoog: menu, logo, de naam van het scherm, en rechts
 *  wat je op elk scherm wilt weten: hoeveel er stuk is, hoe oud de laatste
 *  meting is, een knop om nu te meten, en of jij seintjes krijgt.
 * =========================================================================== */

export default function Shell({ children }: { children: ReactNode }) {
  const { pad } = useRoute()
  const kop = useKop()
  const [menu, setMenu] = useState(false)
  const terugval = useStaat((s) => s.terugval)

  useEffect(() => { setMenu(false) }, [pad])

  return (
    <div className="shell">
      <header className="topbalk">
        <button type="button" className="menuknop" aria-expanded={menu} aria-label="Menu" onClick={() => setMenu((x) => !x)}>
          {menu ? <X size={18} /> : <Menu size={18} />}
        </button>
        <button type="button" className="topbalk-merk" onClick={() => navigeer('/')} title="Naar het overzicht">
          <Logo breedte={96} />
          <span className="topbalk-app">Health</span>
        </button>
        {kop.titel && (
          <div className="topbalk-plek">
            <span className="topbalk-punt">·</span>
            <b>{kop.titel}</b>
            {kop.sub && <span className="topbalk-sub">{kop.sub}</span>}
          </div>
        )}
        <span className="topbalk-rek" />
        <div className="topbalk-rechts">
          <Stand />
          <MeetNu />
          <Seintjes />
          <IkMenu />
        </div>
      </header>

      {terugval && (
        <div className="terugval-balk" role="alert">
          <Siren size={15} /> Het dashboard antwoordt niet. Je bent binnengelaten op je laatste toegang; wat je ziet is wat Health zelf heeft
          gemeten en bewaard.
        </div>
      )}

      <main id="inhoud" className="werkvlak">{children}</main>

      {menu && <Lanceerder onSluit={() => setMenu(false)} />}
      <Meldingen />
    </div>
  )
}

/** Hoeveel er stuk is, en hoe oud de meting. Klik = naar de storingen. */
function Stand() {
  const { overzicht } = useOverzicht()
  const nu = useNu()
  if (!overzicht) return null
  const stil = !overzicht.at || nu - overzicht.at > overzicht.stilNa
  const storing = overzicht.telling.storing
  const s = stil ? 'onbekend' : storing ? 'storing' : overzicht.telling.traag ? 'traag' : 'werkt'
  return (
    <Link naar={storing ? '/storingen' : '/'} className={`stand-pil s-${s}`} title={overzicht.at ? `Laatste meting ${geleden(overzicht.at, nu)}` : 'Nog niets gemeten'}>
      <StaatIcoon s={s} grootte={14} />
      <span>{stil ? 'Meter staat stil' : storing ? `${storing} storing${storing === 1 ? '' : 'en'}` : overzicht.telling.traag ? `${overzicht.telling.traag} traag` : 'Alles werkt'}</span>
      <span className="stand-tijd verberg-smal">{geleden(overzicht.at, nu)}</span>
    </Link>
  )
}

function MeetNu() {
  const [bezig, setBezig] = useState(false)
  return (
    <Knop soort="stil" klein bezig={bezig} icoon={<RefreshCw size={15} />} title="Nu een ronde meten (duurt een paar seconden tot een halve minuut)"
      onClick={async () => {
        setBezig(true)
        try {
          const x = await api.ronde()
          await laadOverzicht()
          meld(`Gemeten in ${(x.duurMs / 1000).toFixed(1)} s: ${x.telling.storing} storing, ${x.telling.traag} traag, ${x.telling.werkt} goed.`, 'ok')
        } catch (e) { meldFout(e) } finally { setBezig(false) }
      }}>
      <span className="verberg-smal">Nu meten</span>
    </Knop>
  )
}

/** De eigen keuze: seintjes in de bel van het dashboard, of niet. */
function Seintjes() {
  const [aan, setAan] = useState<boolean | null>(null)
  useEffect(() => { api.voorkeur().then((v) => setAan(v.seintjes)).catch(() => setAan(null)) }, [])
  if (aan === null) return null
  return (
    <Knop soort="stil" klein icoon={aan ? <Bell size={15} /> : <BellOff size={15} />}
      title={aan ? 'Je krijgt een seintje in het dashboard bij een storing. Klik om uit te zetten.' : 'Je krijgt geen seintjes. Klik om ze aan te zetten.'}
      onClick={async () => {
        try {
          const v = await api.zetVoorkeur(!aan)
          setAan(v.seintjes)
          meld(v.seintjes ? 'Je krijgt voortaan een seintje in het dashboard bij een storing.' : 'Je krijgt geen seintjes meer.', 'info')
        } catch (e) { meldFout(e) }
      }} />
  )
}

/* ------------------------------------------------------------------ *
 *  Het grote menu
 * ------------------------------------------------------------------ */

interface Plek { naar: string; label: string; icoon: ReactNode; teller?: number; extern?: boolean }

function Lanceerder({ onSluit }: { onSluit: () => void }) {
  const { overzicht } = useOverzicht()
  const { pad } = useRoute()
  const [filter, setFilter] = useState('')
  const veld = useRef<HTMLInputElement>(null)

  useEffect(() => {
    veld.current?.focus()
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') onSluit() }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [onSluit])

  const groepen = useMemo(() => {
    const perGroep = new Map<string, number>()
    for (const m of overzicht?.metingen ?? []) if (m.s === 'storing') perGroep.set(m.groep, (perGroep.get(m.groep) ?? 0) + 1)
    const g: { titel: string; plekken: Plek[] }[] = [
      { titel: 'Health', plekken: [
        { naar: '/', label: 'Overzicht', icoon: <Activity size={17} /> },
        { naar: '/storingen', label: 'Storingen', icoon: <Siren size={17} />, teller: overzicht?.storingen.length || undefined },
        { naar: '/logboek', label: 'Logboek', icoon: <ScrollText size={17} /> },
        { naar: '/database', label: 'Database', icoon: <Database size={17} /> },
        { naar: '/pcs', label: "Pc's", icoon: <Computer size={17} /> },
        { naar: '/configuratie', label: 'Configuratie', icoon: <Settings2 size={17} /> },
      ] },
      { titel: 'Systemen', plekken: GROEPEN.map((x) => ({
        naar: `/?groep=${x.key}`, label: x.naam, icoon: <span className="groep-stip" aria-hidden />, teller: perGroep.get(x.key),
      })) },
      { titel: 'Truckwash1', plekken: [
        { naar: 'https://truckwash-workspace.com/', label: 'Het dashboard', icoon: <LayoutDashboard size={17} />, extern: true },
        { naar: 'https://projects.truckwash-workspace.com/', label: 'Projects', icoon: <FolderKanban size={17} />, extern: true },
        { naar: 'https://academy.truckwash-workspace.com/', label: 'Academy', icoon: <GraduationCap size={17} />, extern: true },
      ] },
    ]
    const f = filter.trim().toLowerCase()
    return f ? g.map((x) => ({ ...x, plekken: x.plekken.filter((p) => p.label.toLowerCase().includes(f)) })).filter((x) => x.plekken.length) : g
  }, [overzicht, filter])

  const huidig = (naar: string) => (naar === '/' ? pad === '/' && !location.search : !naar.includes('?') && pad.startsWith(naar))

  return createPortal(
    <>
      <div className="launcher-sluier" onClick={onSluit} />
      <nav className="launcher" aria-label="Alles in Health">
        <div className="launcher-kop">
          <h2>Health</h2>
          <p>De stand van alle systemen.</p>
          <div className="zoekveld launcher-filter">
            <Search size={15} />
            <input ref={veld} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter dit menu…"
              onKeyDown={(e) => { if (e.key === 'Enter') { const eerste = groepen[0]?.plekken[0]; if (eerste) ga(eerste) } }} />
          </div>
        </div>
        <div className="launcher-body">
          {groepen.map((g) => (
            <div key={g.titel} className="launcher-groep">
              <h3>{g.titel}</h3>
              {g.plekken.map((p) => (
                <button key={p.naar} type="button" className="launcher-item" aria-current={huidig(p.naar) ? 'page' : undefined} onClick={() => ga(p)}>
                  {p.icoon}
                  <span className="naam">{p.label}</span>
                  {!!p.teller && <span className="teller" title="Storingen">{p.teller}</span>}
                  {p.extern && <ExternalLink size={13} />}
                </button>
              ))}
            </div>
          ))}
          {!groepen.length && <p className="stil">Niets gevonden voor “{filter}”.</p>}
        </div>
      </nav>
    </>,
    document.body,
  )

  function ga(p: Plek) {
    if (p.extern) { window.open(p.naar, '_blank', 'noopener,noreferrer'); return }
    onSluit()
    navigeer(p.naar)
  }
}

/* ------------------------------------------------------------------ *
 *  Wie je bent, het thema, en weg
 * ------------------------------------------------------------------ */

function IkMenu() {
  const ik = useStaat((s) => s.ik)!
  const { keuze } = useThema()
  const [open, setOpen] = useState(false)
  const knop = useRef<HTMLButtonElement>(null)
  const [plek, setPlek] = useState<{ top: number; right: number } | null>(null)

  useEffect(() => {
    if (!open || !knop.current) return
    const r = knop.current.getBoundingClientRect()
    setPlek({ top: r.bottom + 6, right: window.innerWidth - r.right })
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [open])

  const ICOON: Record<ThemaKeuze, ReactNode> = { systeem: <Monitor size={15} />, licht: <Sun size={15} />, donker: <Moon size={15} /> }
  const rol = ik.rollen.includes('developer') ? 'Ontwikkelaar' : ik.rollen.includes('management') ? 'Management' : ''

  return (
    <>
      <button ref={knop} type="button" className={`menu-trigger ${open ? 'open' : ''}`} onClick={() => setOpen((x) => !x)} aria-label="Jouw menu">
        <span className="menu-av">{initialen(ik.naam)}</span>
        <ChevronDown size={14} className="pijl" />
      </button>
      {open && plek && createPortal(
        <>
          <div className="uitklap-vang" onMouseDown={() => setOpen(false)} />
          <div className="menu-panel" style={{ top: plek.top, right: plek.right }}>
            <div className="menu-persoon-kop">
              <strong>{ik.naam}</strong>
              <span>{[rol, ik.email].filter(Boolean).join(' · ')}</span>
            </div>
            <div className="menu-scheiding" />
            <div className="menu-groep-titel">Weergave</div>
            {(['systeem', 'licht', 'donker'] as ThemaKeuze[]).map((k) => (
              <button key={k} type="button" className={`menu-item ${keuze === k ? 'aan' : ''}`} onClick={() => kiesThema(k)}>
                <span className="ico">{ICOON[k]}</span>
                <span className="tekst"><strong>{THEMA_LABELS[k]}</strong></span>
                {keuze === k && <span className="menu-vink">✓</span>}
              </button>
            ))}
            <div className="menu-scheiding" />
            <a className="menu-item" href="https://truckwash-workspace.com/" target="_blank" rel="noopener noreferrer">
              <span className="ico"><LayoutDashboard size={15} /></span>
              <span className="tekst"><strong>Naar het dashboard</strong></span>
            </a>
            <button type="button" className="menu-item danger" onClick={() => { void uitloggen().then(() => location.assign('/')) }}>
              <span className="ico"><LogOut size={15} /></span>
              <span className="tekst"><strong>Uitloggen</strong></span>
            </button>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
